import React, { useCallback, useEffect, useRef, useState } from "react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/Image";
import MovieIcon from "@mui/icons-material/Movie";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { CircularProgress } from "@mui/material";
import type { TaskFile } from "../types/types";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { FilePreviewer } from "./FilePreviewer.tsx";
import {
  buildFileDownloadUrl,
  fetchTaskFiles,
  formatFileSize,
  getFilePreviewKind,
  removeTaskFileLink,
  uploadTaskFile,
} from "../utils/fileApi.ts";

interface TaskAttachmentsProps {
  taskId: string;
  honbuId: number;
  color: PaletteColor;
  /** 変更（アップロード・削除）後に呼ばれる。一覧側の再取得等に使用 */
  onChanged?: () => void;
  /** false にすると閲覧専用（アップロード・削除ボタン非表示） */
  canEdit?: boolean;
  /** true にするとファイル一覧を2カラムで表示する（ワイドパネル用） */
  twoColumns?: boolean;
}

export function FileKindIcon({ fileName, fontSize = "small" }: { fileName: string; fontSize?: "small" | "medium" }) {
  switch (getFilePreviewKind(fileName)) {
    case "image":
      return <ImageIcon fontSize={fontSize} className="text-emerald-500" />;
    case "video":
      return <MovieIcon fontSize={fontSize} className="text-violet-500" />;
    case "pdf":
      return <PictureAsPdfIcon fontSize={fontSize} className="text-red-500" />;
    case "office":
      return <DescriptionIcon fontSize={fontSize} className="text-blue-500" />;
    default:
      return <InsertDriveFileIcon fontSize={fontSize} className="text-gray-400" />;
  }
}

/**
 * タスクの添付ファイル管理セクション（F017）。
 * 自身で一覧を取得・保持するため、親コンポーネントのデータフローに依存しない。
 */
export function TaskAttachments({
  taskId,
  honbuId,
  color,
  onChanged,
  canEdit = true,
  twoColumns = false,
}: TaskAttachmentsProps) {
  const isDarkMode = useDarkMode();
  const accent = PaletteColorHex[color] ?? PaletteColorHex.Blue;
  const [files, setFiles] = useState<TaskFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewFile, setPreviewFile] = useState<TaskFile | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaskFile | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    try {
      const data = await fetchTaskFiles(taskId, honbuId);
      setFiles(data);
    } catch {
      setFiles([]);
    }
  }, [taskId, honbuId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleFilesSelected = async (selected: FileList | null) => {
    if (!selected || selected.length === 0) return;
    setErrorMessage("");
    setIsUploading(true);
    try {
      for (const file of Array.from(selected)) {
        await uploadTaskFile(taskId, honbuId, file);
      }
      await reload();
      onChanged?.();
    } catch (err) {
      console.error("Failed to upload attachment:", err);
      setErrorMessage(err instanceof Error && err.message ? err.message : "アップロードに失敗しました。");
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await removeTaskFileLink(taskId, target.fileId, honbuId);
      setFiles((prev) => prev.filter((f) => f.fileId !== target.fileId));
      onChanged?.();
    } catch (err) {
      console.error("Failed to remove attachment:", err);
      setErrorMessage("添付の削除に失敗しました。");
    }
  };

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <div
          className={`flex items-center gap-1 text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          <AttachFileIcon sx={{ fontSize: 16 }} />
          添付ファイル
          {files.length > 0 && <span>({files.length})</span>}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={isUploading}
            className={`p-0.5 rounded-full transition-colors disabled:opacity-50 ${
              isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
            }`}
            style={{ color: accent }}
            title="ファイルを追加"
            aria-label="ファイルを追加"
          >
            {isUploading ? (
              <CircularProgress size={16} sx={{ color: accent, display: "block" }} />
            ) : (
              <ControlPointOutlinedIcon sx={{ fontSize: 18, display: "block" }} />
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handleFilesSelected(e.target.files)}
      />

      {errorMessage && (
        <p className="text-xs text-red-500 mb-1">{errorMessage}</p>
      )}

      {files.length > 0 && (
        <ul className={twoColumns ? "grid grid-cols-2 gap-1" : "space-y-1"}>
          {files.map((file) => (
            <li
              key={file.fileId}
              className={`flex items-center gap-2 px-2 py-1.5 rounded border min-w-0 ${
                isDarkMode
                  ? "border-gray-700 bg-[#1e1e1e] hover:bg-gray-800"
                  : "border-gray-200 bg-gray-50 hover:bg-gray-100"
              }`}
            >
              <FileKindIcon fileName={file.fileName} />
              <button
                type="button"
                onClick={() => setPreviewFile(file)}
                className={`flex-1 min-w-0 text-left text-sm truncate hover:underline ${
                  isDarkMode ? "text-gray-200" : "text-gray-700"
                }`}
                title={file.fileName}
              >
                {file.fileName}
              </button>
              <span
                className={`text-xs shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              >
                {formatFileSize(file.fileSize)}
              </span>
              <a
                href={buildFileDownloadUrl(file.fileId, honbuId)}
                className={`p-1 rounded shrink-0 ${
                  isDarkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-200"
                }`}
                aria-label="ダウンロード"
                title="ダウンロード"
              >
                <DownloadIcon sx={{ fontSize: 18 }} />
              </a>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setDeleteTarget(file)}
                  className="p-1 rounded shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                  aria-label="添付を削除"
                  title="このタスクから添付を削除"
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {previewFile && (
        <FilePreviewer
          file={previewFile}
          honbuId={honbuId}
          color={color}
          onClose={() => setPreviewFile(null)}
        />
      )}

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="添付の削除"
        message={`「${deleteTarget?.fileName ?? ""}」をこのタスクから削除しますか？\n（コピーやテンプレートで共有されている他のタスクの添付には影響しません）`}
        confirmText="削除する"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        color={color}
        type="danger"
      />
    </div>
  );
}
